import { Suspense, lazy } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { m } from 'motion/react';
import { Flame, GraduationCap, Dumbbell, Music, User, Users } from 'lucide-react';
import type { CharacterId } from '@audigoal/core';
import { Chip } from '@/components';
import { PickIcon } from '@/components/PickIcon';
import { useSfx } from '@/lib/use-sfx';
import { useMotionPrefs } from '@/lib/use-motion-prefs';
import { useSession } from './session-context';
import { OfflineBanner } from './OfflineBanner';
import { ConsentSheet } from '@/features/privacy/ConsentSheet';
import { InstrumentSwitcher } from './InstrumentSwitcher';
import { cn } from '@/lib/cn';
import { t } from '@/i18n';

const TABS = [
  { to: '/learn', label: t('nav.learn'), Icon: GraduationCap },
  { to: '/practice', label: t('nav.practice'), Icon: Dumbbell },
  { to: '/songs', label: t('nav.songs'), Icon: Music },
  { to: '/ensemble', label: t('nav.ensemble'), Icon: Users, social: true },
  { to: '/profile', label: t('nav.profile'), Icon: User },
] as const;

/** The age gate hides the social tabs outright rather than disabling them. */
export function tabsFor(minor: boolean): readonly (typeof TABS)[number][] {
  return minor ? TABS.filter((tab) => !('social' in tab && tab.social)) : TABS;
}

/**
 * The Profile tab is the learner's companion rather than a generic person icon
 * (plan doc 16). Lazily, with the icon as the fallback: the rigs are 50 kB and
 * the tab bar is in the entry chunk, so the icon shows at once and the
 * character swaps in behind it.
 */
const CompanionTab = lazy(async () => {
  const { Character } = await import('@/characters/Character');
  return {
    default: ({ character }: { character: CharacterId }) => (
      <Character character={character} size={26} animate={false} decorative />
    ),
  };
});

function TopBar() {
  const { profile } = useSession();
  const navigate = useNavigate();

  return (
    <header className="safe-top sticky top-0 z-30 border-b-2 border-line bg-bg/95 backdrop-blur">
      <div className="mx-auto flex h-[var(--topbar-h)] max-w-[var(--content-max)] items-center justify-between gap-1 px-2">
        <InstrumentSwitcher />

        <div className="flex items-center">
          <Chip
            tone="streak"
            icon={<Flame size={20} strokeWidth={2.5} />}
            onClick={() => navigate('/streak')}
            aria-label={t('topbar.streak', { n: profile.streakCurrent })}
          >
            {profile.streakCurrent}
          </Chip>
          <Chip
            tone="gem"
            icon={<PickIcon size={20} />}
            onClick={() => navigate('/shop')}
            aria-label={t('topbar.picks', { n: profile.gems })}
          >
            {profile.gems}
          </Chip>
        </div>
      </div>
    </header>
  );
}

function TabBar({ minor, companion }: { minor: boolean; companion: CharacterId }) {
  const tabs = tabsFor(minor);
  const sfx = useSfx();
  const { reduced } = useMotionPrefs();
  return (
    <nav
      aria-label={t('a11y.primaryNav')}
      className={cn(
        'safe-bottom fixed inset-x-0 bottom-0 z-30 border-t-2 border-line bg-bg',
        'lg:inset-y-0 lg:right-auto lg:left-0 lg:w-56 lg:border-t-0 lg:border-r-2 lg:pt-6',
      )}
    >
      <ul
        className={cn(
          'mx-auto flex h-[var(--tabbar-h)] max-w-[var(--content-max)] items-stretch',
          'lg:h-auto lg:flex-col lg:gap-1 lg:px-3',
        )}
      >
        {tabs.map(({ to, label, Icon }) => (
          <li key={to} className="flex-1 lg:flex-none">
            <NavLink
              to={to}
              onClick={() => sfx.tap()}
              className={({ isActive }) =>
                cn(
                  'flex h-full flex-col items-center justify-center gap-0.5 rounded-lg text-xs font-bold',
                  'transition-transform active:scale-95',
                  'lg:flex-row lg:justify-start lg:gap-3 lg:px-3 lg:py-3 lg:text-base',
                  isActive ? 'text-brand-ink' : 'text-text-2 hover:text-text',
                )
              }
            >
              {({ isActive }) => (
                <>
                  {/* The bounce is keyed on whether this tab is the active one,
                      so it plays when the tab is arrived at — including by the
                      back button — rather than only when it is tapped. */}
                  <m.span
                    key={isActive ? 'on' : 'off'}
                    aria-hidden
                    className="block"
                    initial={reduced || !isActive ? false : { scale: 1.2 }}
                    animate={{ scale: isActive ? 1.1 : 1 }}
                    transition={
                      reduced ? { duration: 0.01 } : { type: 'spring', stiffness: 400, damping: 18 }
                    }
                  >
                    {to === '/profile' ? (
                      <Suspense fallback={<Icon size={24} strokeWidth={2} aria-hidden />}>
                        <CompanionTab character={companion} />
                      </Suspense>
                    ) : (
                      <Icon size={24} strokeWidth={isActive ? 2.75 : 2} aria-hidden />
                    )}
                  </m.span>
                  <span>{label}</span>
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/** Shell for the five main tabs. Full-screen routes (lesson, song) opt out of this. */
export function AppLayout() {
  const { profile } = useSession();
  const { pathname } = useLocation();
  const { reduced } = useMotionPrefs();
  return (
    <div className="flex min-h-dvh flex-col lg:pl-56">
      <OfflineBanner />
      {/* Asked once, after the first finished lesson; see lib/consent.ts. */}
      <ConsentSheet />
      <TopBar />
      <main
        id="main"
        aria-label={t('a11y.mainContent')}
        className="mx-auto w-full max-w-[var(--content-max)] flex-1 px-[var(--gutter)] pb-[calc(var(--tabbar-h)+env(safe-area-inset-bottom,0px)+16px)] pt-4 lg:pb-8"
      >
        {/* Keyed on the path so a tab change cross-fades (plan doc 15). Only
            the path, not the full location: a query change on the same screen
            is a filter being applied, not a new page arriving. */}
        <m.div
          key={pathname}
          initial={reduced ? { opacity: 0 } : { opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: reduced ? 0.01 : 0.2 }}
        >
          <Outlet />
        </m.div>
      </main>
      <TabBar minor={profile.flags.minor} companion={profile.companionCharacter} />
    </div>
  );
}
